/**
 * Subscription Analytics Dashboard Component
 *
 * MRR, Churn, LTV, and Revenue metrics visualization
 */

import * as React from 'react';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Users,
  CreditCard,
  ArrowUpRight,
  ArrowDownRight,
  BarChart3,
  PieChart,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  useSubscriptionMetrics,
  useMRRTrend,
  useChurnAnalysis,
  useLTV,
  useRevenueByPeriod,
} from '@/hooks/useAdmin';

interface MetricCardProps {
  title: string;
  value: string;
  description?: string;
  icon: React.ElementType;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  loading?: boolean;
}

function MetricCard({
  title,
  value,
  description,
  icon: Icon,
  trend,
  loading,
}: MetricCardProps): React.JSX.Element {
  if (loading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-4" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-8 w-32" />
          <Skeleton className="mt-2 h-3 w-20" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {trend && (
          <div className="flex items-center mt-1">
            {trend.isPositive ? (
              <ArrowUpRight className="h-4 w-4 text-green-500" />
            ) : (
              <ArrowDownRight className="h-4 w-4 text-red-500" />
            )}
            <span
              className={cn(
                'text-xs font-medium',
                trend.isPositive ? 'text-green-500' : 'text-red-500'
              )}
            >
              {trend.value.toFixed(1)}%
            </span>
            {description && (
              <span className="text-xs text-muted-foreground ml-1">{description}</span>
            )}
          </div>
        )}
        {!trend && description && (
          <p className="text-xs text-muted-foreground mt-1">{description}</p>
        )}
      </CardContent>
    </Card>
  );
}

interface MRRChartProps {
  data: Array<{
    date: string;
    mrr: number;
    mrrFormatted: string;
    subscriptionCount: number;
  }>;
}

function MRRChart({ data }: MRRChartProps): React.JSX.Element {
  const maxMRR = Math.max(...data.map((d) => d.mrr));

  return (
    <div className="space-y-2">
      <div className="flex items-end gap-1 h-32">
        {data.slice(-14).map((point, idx) => {
          const height = maxMRR > 0 ? (point.mrr / maxMRR) * 100 : 0;
          return (
            <div
              key={idx}
              className="flex-1 bg-primary/20 hover:bg-primary/40 rounded-t transition-colors group relative"
              style={{ height: `${Math.max(height, 4)}%` } as React.CSSProperties}
            >
              <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 hidden group-hover:block bg-popover border rounded px-2 py-1 text-xs whitespace-nowrap z-10">
                <div className="font-medium">{point.mrrFormatted}</div>
                <div className="text-muted-foreground">
                  {new Date(point.date).toLocaleDateString()}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>{data[0] ? new Date(data[0].date).toLocaleDateString() : ''}</span>
        <span>{data.at(-1) ? new Date(data.at(-1)!.date).toLocaleDateString() : ''}</span>
      </div>
    </div>
  );
}

interface PlanDistributionProps {
  distribution: Record<string, number>;
}

const PLAN_COLORS: Record<string, string> = {
  free: 'bg-gray-400',
  starter: 'bg-blue-400',
  professional: 'bg-purple-400',
  enterprise: 'bg-amber-400',
};

function PlanDistribution({ distribution }: PlanDistributionProps): React.JSX.Element {
  const total = Object.values(distribution).reduce((sum, count) => sum + count, 0);

  return (
    <div className="space-y-3">
      {Object.entries(distribution).map(([plan, count]) => {
        const percentage = total > 0 ? (count / total) * 100 : 0;
        const color = PLAN_COLORS[plan] || 'bg-gray-400';

        return (
          <div key={plan} className="space-y-1">
            <div className="flex items-center justify-between text-sm">
              <span className="capitalize font-medium">{plan}</span>
              <span className="text-muted-foreground">
                {count} ({percentage.toFixed(1)}%)
              </span>
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div
                className={cn('h-full rounded-full transition-all', color)}
                style={{ width: `${percentage}%` } as React.CSSProperties}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

interface RevenueChartProps {
  data: Array<{
    period: string;
    revenue: number;
    revenueFormatted: string;
    subscriptionCount: number;
  }>;
}

function RevenueChart({ data }: RevenueChartProps): React.JSX.Element {
  const maxRevenue = Math.max(...data.map((d) => d.revenue));

  return (
    <div className="space-y-2">
      <div className="flex items-end gap-2 h-40">
        {data.map((point, idx) => {
          const height = maxRevenue > 0 ? (point.revenue / maxRevenue) * 100 : 0;
          return (
            <div key={idx} className="flex-1 flex flex-col items-center gap-1">
              <div
                className="w-full bg-primary hover:bg-primary/80 rounded-t transition-colors group relative"
                style={{ height: `${Math.max(height, 4)}%` } as React.CSSProperties}
              >
                <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 hidden group-hover:block bg-popover border rounded px-2 py-1 text-xs whitespace-nowrap z-10">
                  <div className="font-medium">{point.revenueFormatted}</div>
                  <div className="text-muted-foreground">{point.subscriptionCount} subs</div>
                </div>
              </div>
              <span className="text-[10px] text-muted-foreground truncate w-full text-center">
                {point.period}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function SubscriptionAnalytics(): React.JSX.Element {
  const [periodDays, setPeriodDays] = React.useState(30);
  const [revenuePeriodType, setRevenuePeriodType] = React.useState<'day' | 'week' | 'month'>('month');

  const { data: metrics, isLoading: metricsLoading } = useSubscriptionMetrics(periodDays);
  const { data: mrrTrend, isLoading: trendLoading } = useMRRTrend(periodDays);
  const { data: churn, isLoading: churnLoading } = useChurnAnalysis(periodDays);
  const { data: ltv, isLoading: ltvLoading } = useLTV();
  const { data: revenue, isLoading: revenueLoading } = useRevenueByPeriod(revenuePeriodType, 12);

  return (
    <div className="space-y-6">
      {/* Period Selector */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Subscription Analytics</h2>
          <p className="text-sm text-muted-foreground">Revenue and subscription metrics</p>
        </div>
        <Select value={String(periodDays)} onValueChange={(v) => setPeriodDays(Number(v))}>
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">7 days</SelectItem>
            <SelectItem value="30">30 days</SelectItem>
            <SelectItem value="90">90 days</SelectItem>
            <SelectItem value="365">1 year</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Key Metrics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          title="Monthly Recurring Revenue"
          value={metrics?.metrics.mrrFormatted || '$0.00'}
          icon={DollarSign}
          loading={metricsLoading}
          description="Total MRR from active subscriptions"
        />
        <MetricCard
          title="Active Subscriptions"
          value={String(metrics?.metrics.activeSubscriptions || 0)}
          icon={CreditCard}
          loading={metricsLoading}
          trend={
            metrics
              ? {
                  value: metrics.metrics.newSubscriptions,
                  isPositive: metrics.metrics.newSubscriptions > 0,
                }
              : undefined
          }
          description="new this period"
        />
        <MetricCard
          title="Churn Rate"
          value={`${metrics?.metrics.churnRate.toFixed(2) || '0'}%`}
          icon={TrendingDown}
          loading={metricsLoading}
          description={`${metrics?.metrics.recentCancellations || 0} cancellations`}
        />
        <MetricCard
          title="Conversion Rate"
          value={`${metrics?.metrics.conversionRate.toFixed(2) || '0'}%`}
          icon={TrendingUp}
          loading={metricsLoading}
          description="Free to paid conversion"
        />
      </div>

      {/* MRR Trend & Plan Distribution */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              MRR Trend
            </CardTitle>
            <CardDescription>Monthly recurring revenue over time</CardDescription>
          </CardHeader>
          <CardContent>
            {trendLoading ? (
              <Skeleton className="h-32 w-full" />
            ) : mrrTrend?.trend && mrrTrend.trend.length > 0 ? (
              <MRRChart data={mrrTrend.trend} />
            ) : (
              <div className="h-32 flex items-center justify-center text-muted-foreground">
                No data available
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <PieChart className="h-4 w-4" />
              Plan Distribution
            </CardTitle>
            <CardDescription>Subscriptions by plan type</CardDescription>
          </CardHeader>
          <CardContent>
            {metricsLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-6 w-full" />
                <Skeleton className="h-6 w-full" />
                <Skeleton className="h-6 w-full" />
              </div>
            ) : metrics?.metrics.planDistribution ? (
              <PlanDistribution distribution={metrics.metrics.planDistribution} />
            ) : (
              <div className="flex items-center justify-center text-muted-foreground py-8">
                No data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* LTV & Churn Analysis */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="h-4 w-4" />
              Customer Lifetime Value
            </CardTitle>
            <CardDescription>Average revenue per customer over lifetime</CardDescription>
          </CardHeader>
          <CardContent>
            {ltvLoading ? (
              <div className="space-y-4">
                <Skeleton className="h-12 w-32" />
                <Skeleton className="h-6 w-full" />
                <Skeleton className="h-6 w-full" />
              </div>
            ) : ltv?.ltv ? (
              <div className="space-y-4">
                <div>
                  <div className="text-3xl font-bold">{ltv.ltv.averageFormatted}</div>
                  <div className="text-sm text-muted-foreground">Average LTV</div>
                </div>
                <div className="space-y-2">
                  <div className="text-sm font-medium">LTV by Plan</div>
                  {Object.entries(ltv.ltv.byPlan).map(([plan, data]) => (
                    <div key={plan} className="flex items-center justify-between text-sm">
                      <span className="capitalize">{plan}</span>
                      <Badge variant="secondary">{data.formatted}</Badge>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center text-muted-foreground py-8">
                No data available
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingDown className="h-4 w-4" />
              Churn Analysis
            </CardTitle>
            <CardDescription>Subscription cancellations by plan</CardDescription>
          </CardHeader>
          <CardContent>
            {churnLoading ? (
              <div className="space-y-4">
                <Skeleton className="h-12 w-32" />
                <Skeleton className="h-6 w-full" />
                <Skeleton className="h-6 w-full" />
              </div>
            ) : churn?.churn ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-3xl font-bold">{churn.churn.rate.toFixed(2)}%</div>
                    <div className="text-sm text-muted-foreground">Churn Rate</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold">{churn.churn.churned}</div>
                    <div className="text-sm text-muted-foreground">Cancellations</div>
                  </div>
                </div>
                {Object.keys(churn.churn.byPlan).length > 0 && (
                  <div className="space-y-2">
                    <div className="text-sm font-medium">Churn by Plan</div>
                    {Object.entries(churn.churn.byPlan).map(([plan, data]) => (
                      <div key={plan} className="flex items-center justify-between text-sm">
                        <span className="capitalize">{plan}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground">{data.churned} churned</span>
                          <Badge variant={data.rate > 5 ? 'destructive' : 'secondary'}>
                            {data.rate.toFixed(1)}%
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center text-muted-foreground py-8">
                No data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Revenue Chart */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <BarChart3 className="h-4 w-4" />
                Revenue Over Time
              </CardTitle>
              <CardDescription>Historical revenue by period</CardDescription>
            </div>
            <Select
              value={revenuePeriodType}
              onValueChange={(v) => setRevenuePeriodType(v as 'day' | 'week' | 'month')}
            >
              <SelectTrigger className="w-24">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="day">Daily</SelectItem>
                <SelectItem value="week">Weekly</SelectItem>
                <SelectItem value="month">Monthly</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {revenueLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : revenue?.revenue && revenue.revenue.length > 0 ? (
            <RevenueChart data={revenue.revenue} />
          ) : (
            <div className="h-40 flex items-center justify-center text-muted-foreground">
              No revenue data available
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default SubscriptionAnalytics;
